import { CategoricalSummaryStatistics } from 'features/dataLayers/load/summaryStats/categoricalStats/categoricalStatisticsDataType'
import { QuantitativeSummaryStatistics } from 'features/dataLayers/load/summaryStats/quantitativeStats/quantitativeStatisticsDataType'
import { MemoizedFunction as LodashMemoizedFunction } from 'lodash'
import React, { useCallback, useRef } from 'react'

import { GeckoBinnedDataBuffer } from './geckoBinnedDataBuffer'

type CdfID = string
type FilePath = string
type CropSelection = string

type MemoizedFunction<Ret> = (() => Promise<Ret>) & LodashMemoizedFunction

type DataEntryCacheValue = {
  generation: number
  getData: MemoizedFunction<GeckoBinnedDataBuffer>
}
export const DeleteCacheValue = 'deleted'
type DeleteCacheValue = typeof DeleteCacheValue

export enum SummaryStatsType {
  Categorical,
  Quantitative,
}

type SummaryStatsCacheData =
  | {
      generation: number
      type: SummaryStatsType.Quantitative
      getData: MemoizedFunction<QuantitativeSummaryStatistics | undefined>
    }
  | {
      generation: number
      type: SummaryStatsType.Categorical
      getData: MemoizedFunction<CategoricalSummaryStatistics | undefined>
    }

export const DataLayerContext = React.createContext<{
  apiURL: string
  cdfDataStatsFetchCache: Map<string, SummaryStatsCacheData>
  cdfDataFetchCache: ReadonlyMap<
    CdfID,
    ReadonlyMap<CropSelection, ReadonlyMap<FilePath, DataEntryCacheValue>>
  >
  setInCdfDataFetchCache: (params: {
    id: CdfID
    filePath: FilePath
    cropSelection: CropSelection
    value: DataEntryCacheValue | DeleteCacheValue
  }) => void
}>({
  apiURL: '',
  cdfDataFetchCache: new Map(),
  cdfDataStatsFetchCache: new Map(),
  setInCdfDataFetchCache: () => void 0,
})

/**
 * A high-level provider component which coordinates calls to Rolodex for
 * heatmap data in particular.
 * @param apiURL      The URL of the Rolodex API to use (dev, prod, etc.)
 * @param children    Children of this component
 * @constructor
 */
export const DataLayerProvider: React.FC<{
  apiURL: string
  children: React.ReactNode
}> = ({ apiURL, children }) => {
  const cdfDataFetchCache = useRef<
    Map<CdfID, Map<CropSelection, Map<FilePath, DataEntryCacheValue>>>
  >(new Map())

  const cdfDataStatsFetchCache = useRef<Map<string, SummaryStatsCacheData>>(
    new Map()
  )
  const setInCdfDataFetchCache = useCallback(
    (params: {
      id: CdfID
      filePath: FilePath
      cropSelection: CropSelection
      value: DataEntryCacheValue | DeleteCacheValue
    }) => {
      const { id, value, cropSelection, filePath } = params

      if (value === DeleteCacheValue) {
        const hasSpecificCdfCache = cdfDataFetchCache.current.has(id)
        if (hasSpecificCdfCache) {
          cdfDataFetchCache.current.get(id)!.delete(filePath)
        }
        return
      }

      const hasSpecificCdfCache = cdfDataFetchCache.current.has(id)
      if (!hasSpecificCdfCache) {
        cdfDataFetchCache.current.set(id, new Map())
      }

      const hasSpecificSelectionCache = cdfDataFetchCache.current
        .get(id)!
        .has(cropSelection)
      if (!hasSpecificSelectionCache) {
        cdfDataFetchCache.current.get(id)!.set(cropSelection, new Map())
      }
      cdfDataFetchCache.current
        .get(id)!
        .get(cropSelection)!
        .set(filePath, value)
    },
    []
  )

  return (
    <DataLayerContext.Provider
      value={{
        apiURL,
        cdfDataFetchCache: cdfDataFetchCache.current,
        cdfDataStatsFetchCache: cdfDataStatsFetchCache.current,
        setInCdfDataFetchCache,
      }}>
      {children}
    </DataLayerContext.Provider>
  )
}
